import { FlowGraphCoordinator } from "core/FlowGraph/flowGraphCoordinator";
import { ParseFlowGraphAsync } from "core/FlowGraph/flowGraphParser";
import { registerGLTFExtension, unregisterGLTFExtension, registeredGLTFExtensions } from "../glTFLoaderExtensionRegistry";
import { AddObjectAccessorToKey, GetPathToObjectConverter } from "./objectModelMapping";
import { _CaptureKHRInteractivityRuntimeInputDefaults, InteractivityGraphToFlowGraphParser } from "./KHR_interactivity/interactivityGraphParser";
import { addToBlockFactory } from "core/FlowGraph/Blocks/flowGraphBlockFactory";
import { Quaternion, Vector3 } from "core/Maths/math.vector.pure";
import { Constants } from "core/Engines/constants";
import { CompositePathToObjectConverter } from "./compositePathToObjectConverter";
import { BabylonScenePathToObjectConverter, BABYLON_SCENE_OBJECT_MODEL_PREFIX, CreateDefaultBabylonSceneObjectModelTree } from "./babylonScenePathToObjectConverter";
import { InteractivityRefPathToObjectConverter } from "./interactivityRefPathToObjectConverter";
import { InteractivityAssetPathToObjectConverter, InteractivityAssetCapabilitiesPrefix, InteractivityLimitsPrefix } from "./interactivityAssetPathToObjectConverter";
import { EventReferencePrefix, DelayReferencePrefix } from "./KHR_interactivity/interactivityReferences";
import { InteractivityHostResolver } from "./KHR_interactivity/interactivityHostResolver";
import { Logger } from "core/Misc/logger";
import { CreateKHRInteractivityDocument, } from "./KHR_interactivity/interactivityGraphModel";
export * from "./KHR_interactivity/interactivityGraphExporter";
export { _CaptureKHRInteractivityRuntimeInputDefaults } from "./KHR_interactivity/interactivityGraphParser";
const NAME = "KHR_interactivity";
const _ImportResults = /*#__PURE__*/ new WeakMap();
/**
 * Gets the completed KHR_interactivity import result for a loaded scene.
 * @param scene scene loaded from the glTF asset
 * @returns the import result, or undefined when the scene has no KHR_interactivity data
 */
export function GetKHRInteractivityImportResult(scene) {
    const results = _ImportResults.get(scene);
    return results?.[results.length - 1];
}
/**
 * Gets every completed KHR_interactivity import result appended to a loaded scene.
 * Results remain in asset load order and each result has a stable {@link IKHRInteractivityImportResult.assetIndex}.
 * @param scene scene containing the loaded glTF assets
 * @returns the scene's import results, or an empty array when it has no KHR_interactivity data
 */
export function GetKHRInteractivityImportResults(scene) {
    return _ImportResults.get(scene) ?? [];
}
/**
 * Loader extension for KHR_interactivity
 */
export class KHR_interactivity {
    /**
     * @internal
     * @param _loader
     */
    constructor(_loader) {
        this._loader = _loader;
        /**
         * The name of this extension.
         */
        this.name = NAME;
        /** Runs after extensions that contribute interactivity operations and object state. */
        this.order = 200;
        this.enabled = this._loader.isExtensionUsed(NAME);
        // avoid starting animations automatically.
        _loader._skipStartAnimationStep = true;
    }
    _initializePathConverter(scene) {
        this._gltfPathConverter = GetPathToObjectConverter(this._loader.gltf, (mapping) => _AddInteractivityObjectModel(scene, this._loader.parent.targetFps, mapping));
        const initialPrefixes = [
            {
                prefix: BABYLON_SCENE_OBJECT_MODEL_PREFIX,
                converter: new BabylonScenePathToObjectConverter(scene, CreateDefaultBabylonSceneObjectModelTree()),
            },
        ];
        const refConverter = new InteractivityRefPathToObjectConverter();
        initialPrefixes.push({ prefix: EventReferencePrefix, converter: refConverter });
        initialPrefixes.push({ prefix: DelayReferencePrefix, converter: refConverter });
        const enabledExtensions = new Set((this._loader.gltf.extensionsUsed ?? []).filter((name) => registeredGLTFExtensions.has(name) && this._loader.parent.extensionOptions[name]?.enabled !== false));
        const assetConverter = new InteractivityAssetPathToObjectConverter(this._loader.gltf, (extensionName) => enabledExtensions.has(extensionName));
        initialPrefixes.push({ prefix: InteractivityAssetCapabilitiesPrefix, converter: assetConverter });
        initialPrefixes.push({ prefix: InteractivityLimitsPrefix, converter: assetConverter });
        const pathConverter = new CompositePathToObjectConverter(initialPrefixes, this._gltfPathConverter);
        this._pathConverter = pathConverter;
        return pathConverter;
    }
    dispose() {
        this._loader = null;
        delete this._gltfPathConverter;
        delete this._pathConverter;
    }
    async onReady() {
        if (!this._loader.babylonScene) {
            return;
        }
        const scene = this._loader.babylonScene;
        const pathConverter = this._initializePathConverter(scene);
        const interactivityDefinition = this._loader.gltf.extensions?.KHR_interactivity;
        if (!interactivityDefinition) {
            // This can technically throw, but it's not a critical error
            return;
        }
        const supportedExtensions = new Set((this._loader.gltf.extensionsUsed ?? []).filter((extensionName) => this._loader.parent.extensionOptions[extensionName]?.enabled !== false));
        const document = CreateKHRInteractivityDocument(interactivityDefinition, supportedExtensions, this._loader.gltf.nodes?.length ?? 0);
        const options = this._loader.parent.extensionOptions[NAME];
        const autoStart = options?.autoStart ?? true;
        const parseOnly = options?.parseOnly ?? false;
        const strictValidation = options?.strictValidation ?? true;
        const importResults = _ImportResults.get(scene) ?? [];
        const result = {
            assetIndex: importResults.length,
            document,
            graphs: [],
            pathConverter,
            glTF: this._loader.gltf,
            scene,
            hostResolver: new InteractivityHostResolver(),
        };
        importResults.push(result);
        _ImportResults.set(scene, importResults);
        await Promise.all(document.graphs.map(async (graphModel) => {
            const graphResult = {
                graph: graphModel,
                diagnostics: graphModel.diagnostics.slice(),
            };
            result.graphs[graphModel.index] = graphResult;
            if (!graphModel.valid && strictValidation) {
                Logger.Error(`KHR_interactivity: rejecting behavior graph #${graphModel.index}: ${graphModel.diagnostics.map((diagnostic) => diagnostic.message).join("; ")}`);
                return;
            }
            try {
                const parser = new InteractivityGraphToFlowGraphParser(strictValidation ? graphModel.effectiveSource : graphModel.source, this._loader.gltf, this._loader.parent.targetFps, graphModel.index, supportedExtensions, strictValidation ? graphModel.declarations : undefined, graphModel.source);
                const serializedFlowGraph = parser.serializeToFlowGraph();
                graphResult.serializedFlowGraph = serializedFlowGraph;
                if (parseOnly) {
                    return;
                }
                const coordinator = new FlowGraphCoordinator({ scene, hostResolver: result.hostResolver });
                coordinator.dispatchEventsSynchronously = false;
                graphResult.coordinator = coordinator;
                graphResult.flowGraph = await ParseFlowGraphAsync(serializedFlowGraph, { coordinator, pathConverter });
                _CaptureKHRInteractivityRuntimeInputDefaults(graphResult.flowGraph);
                if (autoStart && graphModel.index === document.defaultGraphIndex) {
                    coordinator.start();
                }
            }
            catch (error) {
                const message = error?.message ?? String(error);
                graphResult.diagnostics.push({
                    path: graphModel.path,
                    message,
                    severity: "error",
                });
                Logger.Error(`KHR_interactivity: rejecting behavior graph #${graphModel.index}: ${message}`);
                graphResult.coordinator?.dispose();
                delete graphResult.coordinator;
                delete graphResult.flowGraph;
                delete graphResult.serializedFlowGraph;
            }
        }));
    }
}
/**
 * @internal
 * populates the object model with the interactivity extension
 */
export function _AddInteractivityObjectModel(scene, targetFps, mapping) {
    // Note - all of those are read-only, as per the specs!
    // active camera rotation
    AddObjectAccessorToKey("/extensions/KHR_interactivity/?/activeCamera/rotation", {
        get: () => {
            if (!scene.activeCamera) {
                return new Quaternion(NaN, NaN, NaN, NaN);
            }
            const quat = Quaternion.FromRotationMatrix(scene.activeCamera.getWorldMatrix()).normalize();
            if (!scene.useRightHandedSystem) {
                quat.w *= -1; // glTF uses right-handed system, while babylon uses left-handed
                quat.x *= -1; // glTF uses right-handed system, while babylon uses left-handed
            }
            return quat;
        },
        type: "Quaternion",
        getTarget: () => scene.activeCamera,
    }, mapping);
    // activeCamera position
    AddObjectAccessorToKey("/extensions/KHR_interactivity/?/activeCamera/position", {
        get: () => {
            if (!scene.activeCamera) {
                return new Vector3(NaN, NaN, NaN);
            }
            const pos = scene.activeCamera.getWorldMatrix().getTranslation(); // not global position
            if (!scene.useRightHandedSystem) {
                pos.x *= -1; // glTF uses right-handed system, while babylon uses left-handed
            }
            return pos;
        },
        type: "Vector3",
        getTarget: () => scene.activeCamera,
    }, mapping);
    // activeCamera projection properties. Per the spec these read-only values are NaN when there is no
    // active camera, or when the active camera does not use the projection type of the requested pointer
    // (all perspective properties are NaN for an orthographic camera, and vice-versa).
    const getActivePerspectiveValue = (compute) => {
        const camera = scene.activeCamera;
        if (!camera || camera.mode === Constants.ORTHOGRAPHIC_CAMERA) {
            return NaN;
        }
        return compute(camera);
    };
    const getActiveOrthographicValue = (compute) => {
        const camera = scene.activeCamera;
        if (!camera || camera.mode !== Constants.ORTHOGRAPHIC_CAMERA) {
            return NaN;
        }
        return compute(camera);
    };
    // perspective/aspectRatio (width over height)
    AddObjectAccessorToKey("/extensions/KHR_interactivity/?/activeCamera/perspective/aspectRatio", {
        get: () => getActivePerspectiveValue((camera) => camera.getEngine().getAspectRatio(camera)),
        type: "number",
        getTarget: () => scene.activeCamera,
    }, mapping);
    // perspective/yfov (vertical field of view, in radians)
    AddObjectAccessorToKey("/extensions/KHR_interactivity/?/activeCamera/perspective/yfov", {
        get: () => getActivePerspectiveValue((camera) => {
            // Babylon stores the vertical fov when fovMode is vertical-fixed (the default and what the glTF
            // loader sets). For a horizontal-fixed camera, convert the horizontal fov to vertical.
            if (camera.fovMode === Constants.FOVMODE_VERTICAL_FIXED) {
                return camera.fov;
            }
            const aspectRatio = camera.getEngine().getAspectRatio(camera);
            return aspectRatio ? 2 * Math.atan(Math.tan(camera.fov / 2) / aspectRatio) : camera.fov;
        }),
        type: "number",
        getTarget: () => scene.activeCamera,
    }, mapping);
    // perspective/znear (distance to the near clipping plane)
    AddObjectAccessorToKey("/extensions/KHR_interactivity/?/activeCamera/perspective/znear", {
        get: () => getActivePerspectiveValue((camera) => camera.minZ),
        type: "number",
        getTarget: () => scene.activeCamera,
    }, mapping);
    // perspective/zfar (distance to the far clipping plane; Babylon uses maxZ === 0 to mean an infinite far plane)
    AddObjectAccessorToKey("/extensions/KHR_interactivity/?/activeCamera/perspective/zfar", {
        get: () => getActivePerspectiveValue((camera) => (camera.maxZ === 0 ? Infinity : camera.maxZ)),
        type: "number",
        getTarget: () => scene.activeCamera,
    }, mapping);
    // orthographic/xmag (half the orthographic width)
    AddObjectAccessorToKey("/extensions/KHR_interactivity/?/activeCamera/orthographic/xmag", {
        get: () => getActiveOrthographicValue((camera) => {
            const halfWidth = camera.getEngine().getRenderWidth() / 2;
            return ((camera.orthoRight ?? halfWidth) - (camera.orthoLeft ?? -halfWidth)) / 2;
        }),
        type: "number",
        getTarget: () => scene.activeCamera,
    }, mapping);
    // orthographic/ymag (half the orthographic height)
    AddObjectAccessorToKey("/extensions/KHR_interactivity/?/activeCamera/orthographic/ymag", {
        get: () => getActiveOrthographicValue((camera) => {
            const halfHeight = camera.getEngine().getRenderHeight() / 2;
            return ((camera.orthoTop ?? halfHeight) - (camera.orthoBottom ?? -halfHeight)) / 2;
        }),
        type: "number",
        getTarget: () => scene.activeCamera,
    }, mapping);
    // orthographic/znear (distance to the near clipping plane)
    AddObjectAccessorToKey("/extensions/KHR_interactivity/?/activeCamera/orthographic/znear", {
        get: () => getActiveOrthographicValue((camera) => camera.minZ),
        type: "number",
        getTarget: () => scene.activeCamera,
    }, mapping);
    // orthographic/zfar (distance to the far clipping plane)
    AddObjectAccessorToKey("/extensions/KHR_interactivity/?/activeCamera/orthographic/zfar", {
        get: () => getActiveOrthographicValue((camera) => camera.maxZ),
        type: "number",
        getTarget: () => scene.activeCamera,
    }, mapping);
    const getAnimationFps = (animation) => animation._babylonAnimationGroup?.targetedAnimations?.[0]?.animation.framePerSecond ?? targetFps;
    // /animations/{} pointers:
    AddObjectAccessorToKey("/animations/{}/extensions/KHR_interactivity/isPlaying", {
        get: (animation) => {
            return animation._babylonAnimationGroup?.isPlaying ?? false;
        },
        type: "boolean",
        getTarget: (animation) => {
            return animation._babylonAnimationGroup;
        },
    }, mapping);
    AddObjectAccessorToKey("/animations/{}/extensions/KHR_interactivity/minTime", {
        get: (animation) => {
            return (animation._babylonAnimationGroup?.from ?? 0) / getAnimationFps(animation);
        },
        type: "number",
        getTarget: (animation) => {
            return animation._babylonAnimationGroup;
        },
    }, mapping);
    AddObjectAccessorToKey("/animations/{}/extensions/KHR_interactivity/maxTime", {
        get: (animation) => {
            return (animation._babylonAnimationGroup?.to ?? 0) / getAnimationFps(animation);
        },
        type: "number",
        getTarget: (animation) => {
            return animation._babylonAnimationGroup;
        },
    }, mapping);
    // playhead
    AddObjectAccessorToKey("/animations/{}/extensions/KHR_interactivity/playhead", {
        get: (animation) => {
            return (animation._babylonAnimationGroup?.getRetainedCurrentFrame() ?? 0) / getAnimationFps(animation);
        },
        type: "number",
        getTarget: (animation) => {
            return animation._babylonAnimationGroup;
        },
    }, mapping);
    AddObjectAccessorToKey("/animations/{}/extensions/KHR_interactivity/virtualPlayhead", {
        get: (animation) => {
            return (animation._babylonAnimationGroup?.getVirtualCurrentFrame() ?? 0) / getAnimationFps(animation);
        },
        type: "number",
        getTarget: (animation) => {
            return animation._babylonAnimationGroup;
        },
    }, mapping);
}
let _RuntimeRegistered = false;
/**
 * @internal
 * Registers KHR_interactivity runtime dependencies without changing the extension registry.
 */
export function _RegisterKHRInteractivityRuntime() {
    if (_RuntimeRegistered) {
        return;
    }
    _RuntimeRegistered = true;
    addToBlockFactory(NAME, "FlowGraphGLTFDataProvider", async () => {
        return (await import("./KHR_interactivity/flowGraphGLTFDataProvider")).FlowGraphGLTFDataProvider;
    });
    addToBlockFactory(NAME, "FlowGraphUnsupportedInteractivityBlock", async () => {
        return (await import("./KHR_interactivity/flowGraphUnsupportedInteractivityBlock")).FlowGraphUnsupportedInteractivityBlock;
    });
    addToBlockFactory(NAME, "FlowGraphObjectReferenceBlock", async () => {
        return (await import("./KHR_interactivity/flowGraphObjectReferenceBlock")).FlowGraphObjectReferenceBlock;
    });
    addToBlockFactory(NAME, "FlowGraphEventReferenceBlock", async () => {
        return (await import("./KHR_interactivity/flowGraphEventReferenceBlock")).FlowGraphEventReferenceBlock;
    });
}
let _Registered = false;
/**
 * Registers the KHR_interactivity glTF loader extension.
 * Safe to call multiple times; only the first call has an effect.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export function RegisterKHR_interactivity() {
    if (_Registered) {
        return;
    }
    _Registered = true;
    _RegisterKHRInteractivityRuntime();
    unregisterGLTFExtension(NAME);
    registerGLTFExtension(NAME, true, (loader) => new KHR_interactivity(loader));
}
//# sourceMappingURL=KHR_interactivity.pure.js.map